import {
  Dispatch,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

export type DocumentPlaybackSpeed = 0.2 | 0.5 | 1 | 2;
export type DocumentPlaybackScrollMode = 'vertical' | 'zPath';

export const DOCUMENT_PLAYBACK_SPEEDS: DocumentPlaybackSpeed[] = [
  0.2, 0.5, 1, 2,
];

export type DocumentPlaybackSettings = {
  viewingGuarantee: boolean;
  documentAutoScroll: boolean;
  documentDefaultSpeed: DocumentPlaybackSpeed;
  documentRequiredCoverage: number;
  documentAttentionThreshold: number;
  documentMinimumQualifiedDurationMs: number;
};

export type DocumentProgressSnapshot = {
  sessionId: string;
  coveragePercent: number;
  qualifiedDurationMs: number;
  minimumQualifiedDurationMs: number;
  bottomReached: boolean;
  speed: DocumentPlaybackSpeed;
  isPlaying: boolean;
  pauseReason: string | null;
};

type FaceIndexStatus = 'idle' | 'running' | 'fallback';
type ZPathPhase = 'reveal' | 'scan' | 'return';

const VERTICAL_COVERAGE_BUCKETS = 40;
const HORIZONTAL_COVERAGE_BUCKETS = 4;
const COVERAGE_BUCKETS =
  VERTICAL_COVERAGE_BUCKETS * HORIZONTAL_COVERAGE_BUCKETS;
const BASE_SCROLL_PIXELS_PER_SECOND = 64;
const FOCUS_REVEAL_SPEED_MULTIPLIER = 0.55;
const Z_PATH_RETURN_SPEED_MULTIPLIER = 7;
const Z_PATH_VERTICAL_OVERLAP_PX = 36;
// Two-stage hysteresis mirroring the video player's watchdog: a fast lane for
// a near-zero attention signal (turned away / left the camera), and a slower
// lane for ordinary low attention so short dips do not stop progress.
const ATTENTION_ABSENT_THRESHOLD = 5;
const ATTENTION_ABSENT_GRACE_MS = 2500;
const ATTENTION_ABSENT_PAUSE_SUSTAIN_MS = 3500;
const ATTENTION_HARD_ABSENT_PAUSE_SUSTAIN_MS = 2500;
const ATTENTION_GRACE_MS = 5000;
const ATTENTION_PAUSE_SUSTAIN_MS = 8000;
const ATTENTION_RESUME_SUSTAIN_MS = 1000;
const ATTENTION_DECISION_SIGNAL_STALE_MS = 3000;
const ATTENTION_DECISION_DEBUG_INTERVAL_MS = 5000;
const AUTOSCROLL_TICK_MS = 50;

const createSessionId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const getViewportScrollLimits = (viewport: HTMLDivElement) => {
  const content = viewport.firstElementChild as HTMLElement | null;

  if (!content) {
    return {
      maximumLeft: Math.max(0, viewport.scrollWidth - viewport.clientWidth),
      maximumTop: Math.max(0, viewport.scrollHeight - viewport.clientHeight),
    };
  }

  return {
    maximumLeft: Math.max(
      0,
      Math.ceil(content.offsetLeft + content.offsetWidth - viewport.clientWidth)
    ),
    maximumTop: Math.max(
      0,
      Math.ceil(
        content.offsetTop + content.offsetHeight - viewport.clientHeight
      )
    ),
  };
};

const setIfChanged = (
  setter: Dispatch<SetStateAction<number>>,
  nextValue: number
) => {
  setter((current) => (current === nextValue ? current : nextValue));
};

export const isDocumentProgressComplete = (
  progress: DocumentProgressSnapshot,
  requiredCoveragePercent: number
) =>
  progress.bottomReached &&
  progress.coveragePercent >= clamp(requiredCoveragePercent, 50, 100) &&
  progress.minimumQualifiedDurationMs > 0 &&
  progress.qualifiedDurationMs >= progress.minimumQualifiedDurationMs;

const normalizePlaybackSpeed = (
  value: DocumentPlaybackSpeed | string | number | undefined
): DocumentPlaybackSpeed => {
  if (value === undefined) {
    return 0.2;
  }

  const numericValue =
    typeof value === 'number' ? value : Number.parseFloat(value);

  if (
    DOCUMENT_PLAYBACK_SPEEDS.includes(numericValue as DocumentPlaybackSpeed)
  ) {
    return numericValue as DocumentPlaybackSpeed;
  }

  if (numericValue === 1.5 || numericValue === 3) {
    return 2;
  }

  return 0.2;
};

export const useDocumentPlayback = ({
  enabled,
  contentKey,
  faceIndexStatus,
  attention,
  decisionAttention,
  decisionSignalAt,
  decisionHardAbsent,
  settings,
  allowUnfocused = false,
  readingWindowHeightPx = 0,
  debug = false,
  scrollMode = 'vertical',
}: {
  enabled: boolean;
  contentKey: string;
  faceIndexStatus: FaceIndexStatus;
  attention: number;
  decisionAttention?: number | null;
  decisionSignalAt?: number | null;
  decisionHardAbsent?: boolean;
  settings: DocumentPlaybackSettings;
  allowUnfocused?: boolean;
  readingWindowHeightPx?: number;
  debug?: boolean;
  scrollMode?: DocumentPlaybackScrollMode;
}) => {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const coverageRef = useRef(new Set<number>());
  const sessionIdRef = useRef(createSessionId());
  const qualifiedDurationRef = useRef(0);
  const bottomReachedRef = useRef(false);
  const lastTickAtRef = useRef(Date.now());
  const lastPublishAtRef = useRef(0);
  const lowAttentionSinceRef = useRef<number | null>(null);
  const lowAttentionModeRef = useRef<'absent' | 'low' | null>(null);
  const hardAbsentSinceRef = useRef<number | null>(null);
  const attentionPausedPlaybackRef = useRef(false);
  const readingWindowBottomRef = useRef(readingWindowHeightPx);
  const recoveredAttentionSinceRef = useRef<number | null>(null);
  const zPathPhaseRef = useRef<ZPathPhase>('reveal');
  const zPathReturnTargetTopRef = useRef<number | null>(null);
  // Mirrors video's lastHighAttentionTime: the clock the grace period is
  // measured from. Initialized to "now", same as the reference, so a fresh
  // mount gets a full grace window before any low-attention progression can
  // start.
  const lastHighAttentionTimeRef = useRef(Date.now());
  const attentionQualifiedRef = useRef(true);
  const decisionAttentionRef = useRef<number>(attention);
  const decisionSignalAtRef = useRef<number | null>(null);
  const lastDecisionLogAtRef = useRef(0);
  const lastDecisionStateRef = useRef('');
  const lastPlaybackGateLogAtRef = useRef(0);
  const lastPlaybackGateStateRef = useRef('');
  const lastDecisionSignalLogAtRef = useRef(0);
  const [speed, setSpeed] = useState<DocumentPlaybackSpeed>(
    normalizePlaybackSpeed(settings.documentDefaultSpeed)
  );
  const [isPlaying, setIsPlaying] = useState(false);
  const [readingWindowBottomPx, setReadingWindowBottomPx] = useState(
    readingWindowHeightPx
  );
  const [snapshot, setSnapshot] = useState<DocumentProgressSnapshot>({
    sessionId: sessionIdRef.current,
    coveragePercent: 0,
    qualifiedDurationMs: 0,
    minimumQualifiedDurationMs: 0,
    bottomReached: false,
    speed: normalizePlaybackSpeed(settings.documentDefaultSpeed),
    isPlaying: false,
    pauseReason: null,
  });
  const snapshotRef = useRef(snapshot);

  const setReadingWindowBottom = useCallback((nextValue: number) => {
    readingWindowBottomRef.current = nextValue;
    setIfChanged(setReadingWindowBottomPx, Math.round(nextValue));
  }, []);

  const publishSnapshot = useCallback(
    (pauseReason: string | null) => {
      const viewport = viewportRef.current;
      const minimumQualifiedDurationMs =
        settings.documentMinimumQualifiedDurationMs > 0
          ? settings.documentMinimumQualifiedDurationMs
          : viewport
            ? Math.max(
                10000,
                Math.round(
                  ((viewport.scrollHeight *
                    Math.max(1, viewport.scrollWidth / viewport.clientWidth)) /
                    (BASE_SCROLL_PIXELS_PER_SECOND *
                      normalizePlaybackSpeed(speed))) *
                    1000 *
                    0.7
                )
              )
            : 0;
      const next: DocumentProgressSnapshot = {
        sessionId: sessionIdRef.current,
        coveragePercent:
          Math.round((coverageRef.current.size / COVERAGE_BUCKETS) * 1000) / 10,
        qualifiedDurationMs: Math.round(qualifiedDurationRef.current),
        minimumQualifiedDurationMs,
        bottomReached: bottomReachedRef.current,
        speed: normalizePlaybackSpeed(speed),
        isPlaying,
        pauseReason,
      };

      snapshotRef.current = next;
      setSnapshot(next);
    },
    [isPlaying, settings.documentMinimumQualifiedDurationMs, speed]
  );

  const getPauseReason = useCallback(() => {
    if (!enabled) {
      return 'tracking-paused';
    }

    if (document.visibilityState !== 'visible') {
      return 'page-hidden';
    }

    if (!allowUnfocused && !document.hasFocus()) {
      return 'page-unfocused';
    }

    if (faceIndexStatus === 'running' && !attentionQualifiedRef.current) {
      if (attentionPausedPlaybackRef.current) {
        return 'attention-absent';
      }

      return 'attention-low';
    }

    return null;
  }, [
    allowUnfocused,
    enabled,
    faceIndexStatus,
    settings.documentAttentionThreshold,
    settings.viewingGuarantee,
  ]);

  const updateReadingWindowBottom = useCallback(
    (viewport: HTMLDivElement | null) => {
      if (!viewport || viewport.clientHeight <= 0) {
        setReadingWindowBottom(readingWindowHeightPx);
        return;
      }

      const baseHeight = clamp(readingWindowHeightPx, 0, viewport.clientHeight);
      setReadingWindowBottom(
        clamp(
          Math.max(readingWindowBottomRef.current, baseHeight),
          baseHeight,
          viewport.clientHeight
        )
      );
    },
    [readingWindowHeightPx, setReadingWindowBottom]
  );

  const resetReadingWindow = useCallback(
    (viewport: HTMLDivElement | null) => {
      const nextBottom = viewport
        ? clamp(readingWindowHeightPx, 0, viewport.clientHeight)
        : readingWindowHeightPx;
      setReadingWindowBottom(nextBottom);
    },
    [readingWindowHeightPx, setReadingWindowBottom]
  );

  const advanceReadingWindow = useCallback(
    (viewport: HTMLDivElement, distance: number) => {
      const baseHeight = clamp(readingWindowHeightPx, 0, viewport.clientHeight);
      const nextBottom = clamp(
        Math.max(readingWindowBottomRef.current, baseHeight) +
          distance * FOCUS_REVEAL_SPEED_MULTIPLIER,
        baseHeight,
        viewport.clientHeight
      );

      setReadingWindowBottom(nextBottom);
      return nextBottom >= viewport.clientHeight - 1;
    },
    [readingWindowHeightPx, setReadingWindowBottom]
  );

  const markVisibleRange = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport || viewport.scrollHeight <= 0) {
      return;
    }

    updateReadingWindowBottom(viewport);

    const start = clamp(
      Math.floor(
        (viewport.scrollTop / viewport.scrollHeight) * VERTICAL_COVERAGE_BUCKETS
      ),
      0,
      VERTICAL_COVERAGE_BUCKETS - 1
    );
    const end = clamp(
      Math.ceil(
        ((viewport.scrollTop + viewport.clientHeight) / viewport.scrollHeight) *
          VERTICAL_COVERAGE_BUCKETS
      ),
      0,
      VERTICAL_COVERAGE_BUCKETS
    );
    const horizontalStart = clamp(
      Math.floor(
        (viewport.scrollLeft / viewport.scrollWidth) *
          HORIZONTAL_COVERAGE_BUCKETS
      ),
      0,
      HORIZONTAL_COVERAGE_BUCKETS - 1
    );
    const horizontalEnd = clamp(
      Math.ceil(
        ((viewport.scrollLeft + viewport.clientWidth) / viewport.scrollWidth) *
          HORIZONTAL_COVERAGE_BUCKETS
      ),
      0,
      HORIZONTAL_COVERAGE_BUCKETS
    );

    for (let row = start; row < end; row += 1) {
      for (let column = horizontalStart; column < horizontalEnd; column += 1) {
        coverageRef.current.add(row * HORIZONTAL_COVERAGE_BUCKETS + column);
      }
    }

    const { maximumTop } = getViewportScrollLimits(viewport);

    if (viewport.scrollTop >= maximumTop - 4) {
      bottomReachedRef.current = true;
    }
  }, [updateReadingWindowBottom]);

  useEffect(() => {
    sessionIdRef.current = createSessionId();
    coverageRef.current = new Set<number>();
    qualifiedDurationRef.current = 0;
    bottomReachedRef.current = false;
    readingWindowBottomRef.current = readingWindowHeightPx;
    zPathPhaseRef.current = 'reveal';
    zPathReturnTargetTopRef.current = null;
    setReadingWindowBottom(readingWindowHeightPx);
    lastTickAtRef.current = Date.now();
    lastPublishAtRef.current = 0;
    lowAttentionSinceRef.current = null;
    lowAttentionModeRef.current = null;
    hardAbsentSinceRef.current = null;
    attentionPausedPlaybackRef.current = false;
    recoveredAttentionSinceRef.current = null;
    lastHighAttentionTimeRef.current = Date.now();
    attentionQualifiedRef.current = true;
    decisionAttentionRef.current = 0;
    decisionSignalAtRef.current = null;
    lastDecisionLogAtRef.current = 0;
    lastDecisionStateRef.current = '';
    lastPlaybackGateLogAtRef.current = 0;
    lastPlaybackGateStateRef.current = '';
    lastDecisionSignalLogAtRef.current = 0;
    setSpeed(normalizePlaybackSpeed(settings.documentDefaultSpeed));
    setIsPlaying(false);
  }, [
    contentKey,
    settings.documentDefaultSpeed,
    settings.viewingGuarantee,
    readingWindowHeightPx,
    setReadingWindowBottom,
  ]);

  const logAttentionDecision = useCallback(
    (
      event: string,
      details: {
        attention: number;
        displayAttention: number;
        signalAgeMs: number | null;
        signalStale: boolean;
        mode: 'absent' | 'low' | 'neutral' | 'high';
        qualified: boolean;
        lowSustainMs: number | null;
        timeSinceHighMs: number;
        playbackPausedByAttention?: boolean;
        decisionHardAbsent?: boolean;
        hardAbsentSinceMs?: number | null;
        isAttentionHigh?: boolean;
        isHardAbsent?: boolean;
        resumeSustainMs?: number | null;
      },
      force = false
    ) => {
      if (!debug) {
        return;
      }

      const now = Date.now();
      const stateKey = `${event}:${details.mode}:${details.qualified}:${details.signalStale}`;

      if (
        !force &&
        lastDecisionStateRef.current === stateKey &&
        now - lastDecisionLogAtRef.current <
          ATTENTION_DECISION_DEBUG_INTERVAL_MS
      ) {
        return;
      }

      lastDecisionStateRef.current = stateKey;
      lastDecisionLogAtRef.current = now;
      console.info(
        `[DocumentPlaybackDebug] Attention decision: event=${event} attention=${details.attention} display=${details.displayAttention} mode=${details.mode} qualified=${details.qualified} hardAbsent=${details.decisionHardAbsent === true} high=${details.isAttentionHigh === true} pausedByAttention=${details.playbackPausedByAttention === true} signalAge=${details.signalAgeMs ?? 'none'} stale=${details.signalStale} lowSustain=${details.lowSustainMs ?? 'none'} resumeSustain=${details.resumeSustainMs ?? 'none'}`,
        {
          event,
          contentKey,
          threshold: settings.documentAttentionThreshold,
          ...details,
        }
      );
    },
    [contentKey, debug, settings.documentAttentionThreshold]
  );

  const releaseAttentionPlaybackGate = useCallback(
    (reason: 'attention-recovered' | 'manual-play') => {
      const now = Date.now();
      const signalAgeMs = decisionSignalAtRef.current
        ? now - decisionSignalAtRef.current
        : null;

      attentionQualifiedRef.current = true;
      attentionPausedPlaybackRef.current = false;
      lowAttentionSinceRef.current = null;
      lowAttentionModeRef.current = null;
      hardAbsentSinceRef.current = null;
      recoveredAttentionSinceRef.current = null;
      lastHighAttentionTimeRef.current = now;

      logAttentionDecision(
        reason,
        {
          attention: decisionAttentionRef.current,
          displayAttention: attention,
          signalAgeMs,
          signalStale:
            signalAgeMs === null ||
            signalAgeMs > ATTENTION_DECISION_SIGNAL_STALE_MS,
          mode: 'high',
          qualified: true,
          lowSustainMs: null,
          timeSinceHighMs: 0,
          playbackPausedByAttention: false,
          decisionHardAbsent,
          hardAbsentSinceMs: null,
          isAttentionHigh: true,
          isHardAbsent: false,
          resumeSustainMs: null,
        },
        true
      );
    },
    [attention, decisionHardAbsent, logAttentionDecision]
  );

  const updateAttentionQualification = useCallback(
    (now: number) => {
      if (!enabled || faceIndexStatus !== 'running') {
        attentionQualifiedRef.current = true;
        lowAttentionSinceRef.current = null;
        lowAttentionModeRef.current = null;
        recoveredAttentionSinceRef.current = null;
        hardAbsentSinceRef.current = null;
        // Give a fresh grace window once tracking resumes, rather than
        // immediately treating a stale "last high" timestamp as overdue.
        lastHighAttentionTimeRef.current = now;
        logAttentionDecision('attention-gate-disabled', {
          attention: decisionAttentionRef.current,
          displayAttention: attention,
          signalAgeMs: null,
          signalStale: true,
          mode: 'neutral',
          qualified: true,
          lowSustainMs: null,
          timeSinceHighMs: 0,
          playbackPausedByAttention: false,
          decisionHardAbsent,
          hardAbsentSinceMs: null,
          isAttentionHigh: false,
          isHardAbsent: false,
        });
        return;
      }

      const currentAttention = decisionAttentionRef.current;
      const signalAt = decisionSignalAtRef.current;
      const signalAgeMs = signalAt ? now - signalAt : null;
      const isSignalStale =
        signalAgeMs === null ||
        signalAgeMs > ATTENTION_DECISION_SIGNAL_STALE_MS;
      const hasLowAttentionGate =
        settings.viewingGuarantee && settings.documentAttentionThreshold > 0;
      const effectiveAttentionThreshold = hasLowAttentionGate
        ? settings.documentAttentionThreshold
        : ATTENTION_ABSENT_THRESHOLD + 1;
      const isAttentionHigh =
        !isSignalStale && currentAttention >= effectiveAttentionThreshold;

      if (isAttentionHigh) {
        lastHighAttentionTimeRef.current = now;
      }

      const timeSinceHighAttention = now - lastHighAttentionTimeRef.current;
      // Missing/stale telemetry is not enough to stop document playback: the
      // gate must see an actual low/absent attention measurement. This keeps
      // server heartbeat gaps from producing false pauses.
      const hasFreshDecisionSignal = !isSignalStale;
      const isAttentionBelowThreshold =
        hasLowAttentionGate &&
        hasFreshDecisionSignal &&
        currentAttention <= settings.documentAttentionThreshold;
      const isAttentionAbsent =
        decisionHardAbsent === true ||
        (hasFreshDecisionSignal &&
          currentAttention <= ATTENTION_ABSENT_THRESHOLD) ||
        (isAttentionBelowThreshold &&
          currentAttention <= ATTENTION_ABSENT_THRESHOLD);
      const isHardAbsent = decisionHardAbsent === true || isAttentionAbsent;
      const lowAttentionMode = isAttentionAbsent ? 'absent' : 'low';
      const lowAttentionGraceMs = isAttentionAbsent
        ? ATTENTION_ABSENT_GRACE_MS
        : ATTENTION_GRACE_MS;
      const lowAttentionSustainMs = isAttentionAbsent
        ? ATTENTION_ABSENT_PAUSE_SUSTAIN_MS
        : ATTENTION_PAUSE_SUSTAIN_MS;
      const isAttentionLow =
        hasLowAttentionGate &&
        !isAttentionHigh &&
        isAttentionBelowThreshold &&
        timeSinceHighAttention >= lowAttentionGraceMs;

      if (isHardAbsent) {
        recoveredAttentionSinceRef.current = null;
        lowAttentionSinceRef.current = null;
        lowAttentionModeRef.current = 'absent';

        if (hardAbsentSinceRef.current === null) {
          hardAbsentSinceRef.current = now;
          logAttentionDecision(
            'hard-absent-start',
            {
              attention: currentAttention,
              displayAttention: attention,
              signalAgeMs,
              signalStale: isSignalStale,
              mode: 'absent',
              qualified: attentionQualifiedRef.current,
              lowSustainMs: 0,
              timeSinceHighMs: timeSinceHighAttention,
              playbackPausedByAttention: attentionPausedPlaybackRef.current,
              decisionHardAbsent,
              hardAbsentSinceMs: 0,
              isAttentionHigh,
              isHardAbsent,
            },
            true
          );
        }

        const hardAbsentSustainMs = now - hardAbsentSinceRef.current;

        if (
          attentionQualifiedRef.current ||
          (!attentionPausedPlaybackRef.current && isPlaying)
        ) {
          if (hardAbsentSustainMs >= ATTENTION_HARD_ABSENT_PAUSE_SUSTAIN_MS) {
            attentionQualifiedRef.current = false;
            attentionPausedPlaybackRef.current = true;
            if (isPlaying) {
              setIsPlaying(false);
            }
            logAttentionDecision(
              'hard-absent-gate-playback',
              {
                attention: currentAttention,
                displayAttention: attention,
                signalAgeMs,
                signalStale: isSignalStale,
                mode: 'absent',
                qualified: false,
                lowSustainMs: hardAbsentSustainMs,
                timeSinceHighMs: timeSinceHighAttention,
                playbackPausedByAttention: attentionPausedPlaybackRef.current,
                decisionHardAbsent,
                hardAbsentSinceMs: hardAbsentSustainMs,
                isAttentionHigh,
                isHardAbsent,
              },
              true
            );
          } else {
            logAttentionDecision('hard-absent-sustain', {
              attention: currentAttention,
              displayAttention: attention,
              signalAgeMs,
              signalStale: isSignalStale,
              mode: 'absent',
              qualified: attentionQualifiedRef.current,
              lowSustainMs: hardAbsentSustainMs,
              timeSinceHighMs: timeSinceHighAttention,
              playbackPausedByAttention: attentionPausedPlaybackRef.current,
              decisionHardAbsent,
              hardAbsentSinceMs: hardAbsentSustainMs,
              isAttentionHigh,
              isHardAbsent,
            });
          }
        }
      } else if (isAttentionLow) {
        // STATE: INATTENTION. Progress toward pausing accrual, reset resume
        // progress.
        recoveredAttentionSinceRef.current = null;
        hardAbsentSinceRef.current = null;
        if (
          lowAttentionSinceRef.current === null ||
          lowAttentionModeRef.current !== lowAttentionMode
        ) {
          lowAttentionSinceRef.current = now;
          lowAttentionModeRef.current = lowAttentionMode;
          logAttentionDecision(
            'inattention-start',
            {
              attention: currentAttention,
              displayAttention: attention,
              signalAgeMs,
              signalStale: isSignalStale,
              mode: lowAttentionMode,
              qualified: attentionQualifiedRef.current,
              lowSustainMs: 0,
              timeSinceHighMs: timeSinceHighAttention,
              decisionHardAbsent,
              isAttentionHigh,
              isHardAbsent,
            },
            true
          );
        }

        const lowSustainMs = now - lowAttentionSinceRef.current;

        if (
          attentionQualifiedRef.current &&
          lowSustainMs >= lowAttentionSustainMs
        ) {
          attentionQualifiedRef.current = false;
          logAttentionDecision(
            'pause-accrual',
            {
              attention: currentAttention,
              displayAttention: attention,
              signalAgeMs,
              signalStale: isSignalStale,
              mode: lowAttentionMode,
              qualified: false,
              lowSustainMs,
              timeSinceHighMs: timeSinceHighAttention,
              decisionHardAbsent,
              isAttentionHigh,
              isHardAbsent,
            },
            true
          );
        } else {
          logAttentionDecision('inattention-sustain', {
            attention: currentAttention,
            displayAttention: attention,
            signalAgeMs,
            signalStale: isSignalStale,
            mode: lowAttentionMode,
            qualified: attentionQualifiedRef.current,
            lowSustainMs,
            timeSinceHighMs: timeSinceHighAttention,
            decisionHardAbsent,
            isAttentionHigh,
            isHardAbsent,
          });
        }
      } else if (isAttentionHigh) {
        // STATE: GAZE. Progress toward resuming accrual, reset pause progress.
        lowAttentionSinceRef.current = null;
        lowAttentionModeRef.current = null;
        hardAbsentSinceRef.current = null;
        recoveredAttentionSinceRef.current ??= now;

        const resumeSustainMs = now - recoveredAttentionSinceRef.current;

        if (
          !attentionQualifiedRef.current &&
          resumeSustainMs >= ATTENTION_RESUME_SUSTAIN_MS
        ) {
          const wasPausedByAttention = attentionPausedPlaybackRef.current;
          releaseAttentionPlaybackGate('attention-recovered');
          if (attentionPausedPlaybackRef.current) {
            setIsPlaying(true);
          }
          if (wasPausedByAttention) {
            setIsPlaying(true);
          }
          logAttentionDecision(
            'resume-accrual',
            {
              attention: currentAttention,
              displayAttention: attention,
              signalAgeMs,
              signalStale: isSignalStale,
              mode: 'high',
              qualified: true,
              lowSustainMs: null,
              timeSinceHighMs: timeSinceHighAttention,
              playbackPausedByAttention: false,
              decisionHardAbsent,
              hardAbsentSinceMs: null,
              isAttentionHigh,
              isHardAbsent,
              resumeSustainMs,
            },
            true
          );
        } else {
          logAttentionDecision('gaze', {
            attention: currentAttention,
            displayAttention: attention,
            signalAgeMs,
            signalStale: isSignalStale,
            mode: 'high',
            qualified: attentionQualifiedRef.current,
            lowSustainMs: null,
            timeSinceHighMs: timeSinceHighAttention,
            playbackPausedByAttention: attentionPausedPlaybackRef.current,
            decisionHardAbsent,
            hardAbsentSinceMs: null,
            isAttentionHigh,
            isHardAbsent,
            resumeSustainMs,
          });
        }
      } else {
        // STATE: NEUTRAL — below threshold, but still inside the grace window
        // since attention was last confirmed high. Hold at rest rather than
        // accumulating partial progress, same as video's neutral band.
        lowAttentionSinceRef.current = null;
        lowAttentionModeRef.current = null;
        hardAbsentSinceRef.current = null;
        recoveredAttentionSinceRef.current = null;
        logAttentionDecision('neutral', {
          attention: currentAttention,
          displayAttention: attention,
          signalAgeMs,
          signalStale: isSignalStale,
          mode: 'neutral',
          qualified: attentionQualifiedRef.current,
          lowSustainMs: null,
          timeSinceHighMs: timeSinceHighAttention,
          playbackPausedByAttention: attentionPausedPlaybackRef.current,
          decisionHardAbsent,
          hardAbsentSinceMs: null,
          isAttentionHigh,
          isHardAbsent,
        });
      }
    },
    [
      attention,
      decisionHardAbsent,
      enabled,
      faceIndexStatus,
      isPlaying,
      logAttentionDecision,
      releaseAttentionPlaybackGate,
      settings.documentAttentionThreshold,
      settings.viewingGuarantee,
    ]
  );

  const logPlaybackGate = useCallback(
    (
      event: string,
      details: {
        hasViewport: boolean;
        isPlaying: boolean;
        pauseReason: string | null;
      }
    ) => {
      if (!debug) {
        return;
      }

      const now = Date.now();
      const signalAt = decisionSignalAtRef.current;
      const signalAgeMs = signalAt ? now - signalAt : null;
      const stateKey = `${event}:${details.pauseReason}:${details.hasViewport}:${details.isPlaying}:${attentionQualifiedRef.current}:${attentionPausedPlaybackRef.current}`;

      if (
        lastPlaybackGateStateRef.current === stateKey &&
        now - lastPlaybackGateLogAtRef.current <
          ATTENTION_DECISION_DEBUG_INTERVAL_MS
      ) {
        return;
      }

      lastPlaybackGateStateRef.current = stateKey;
      lastPlaybackGateLogAtRef.current = now;
      console.info(
        `[DocumentPlaybackDebug] Playback gate: event=${event} pauseReason=${details.pauseReason ?? 'none'} hasViewport=${details.hasViewport} isPlaying=${details.isPlaying} attention=${decisionAttentionRef.current} display=${attention} qualified=${attentionQualifiedRef.current} pausedByAttention=${attentionPausedPlaybackRef.current} face=${faceIndexStatus} signalAge=${signalAgeMs ?? 'none'}`,
        {
          event,
          contentKey,
          ...details,
          attention: decisionAttentionRef.current,
          attentionPausedPlayback: attentionPausedPlaybackRef.current,
          attentionQualified: attentionQualifiedRef.current,
          displayAttention: attention,
          faceIndexStatus,
          signalAgeMs,
        }
      );
    },
    [attention, contentKey, debug, faceIndexStatus]
  );

  // Two-stage hysteresis, mirroring video's isAttentionLow/isAttentionHigh
  // state machine in indexes-service.tsx. "Pause" here only means "stop
  // accruing qualified reading time" (attentionQualifiedRef) — there is no
  // video element to literally pause. The decision uses the fast/raw attention
  // channel while the UI can keep displaying smoothed attention.
  useEffect(() => {
    const nextDecisionAttention =
      typeof decisionAttention === 'number' &&
      Number.isFinite(decisionAttention)
        ? decisionAttention
        : attention;

    decisionAttentionRef.current = nextDecisionAttention;
    decisionSignalAtRef.current = decisionSignalAt ?? Date.now();
    if (debug) {
      const now = Date.now();
      const signalAt = decisionSignalAtRef.current;
      const signalAgeMs = signalAt ? now - signalAt : null;

      if (
        now - lastDecisionSignalLogAtRef.current >=
        ATTENTION_DECISION_DEBUG_INTERVAL_MS
      ) {
        lastDecisionSignalLogAtRef.current = now;
        console.info('[DocumentPlaybackDebug] Decision signal applied:', {
          attention: nextDecisionAttention,
          attentionPausedPlayback: attentionPausedPlaybackRef.current,
          attentionQualified: attentionQualifiedRef.current,
          contentKey,
          decisionHardAbsent,
          displayAttention: attention,
          enabled,
          faceIndexStatus,
          isPlaying,
          signalAgeMs,
        });
      }
    }
    updateAttentionQualification(Date.now());
  }, [
    attention,
    contentKey,
    debug,
    decisionAttention,
    decisionHardAbsent,
    decisionSignalAt,
    enabled,
    faceIndexStatus,
    isPlaying,
    updateAttentionQualification,
  ]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let frameId = 0;
    const tick = () => {
      const now = Date.now();
      const elapsedMs = Math.min(1000, now - lastTickAtRef.current);
      lastTickAtRef.current = now;
      updateAttentionQualification(now);
      const pauseReason = getPauseReason();
      const viewport = viewportRef.current;

      if (!pauseReason && viewport) {
        qualifiedDurationRef.current += elapsedMs;
        markVisibleRange();
      } else {
        logPlaybackGate('qualified-time-skipped', {
          hasViewport: Boolean(viewport),
          isPlaying,
          pauseReason,
        });
      }

      if (now - lastPublishAtRef.current >= 500) {
        lastPublishAtRef.current = now;
        publishSnapshot(pauseReason);
      }
      frameId = window.requestAnimationFrame(tick);
    };

    frameId = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frameId);
  }, [
    enabled,
    getPauseReason,
    isPlaying,
    logPlaybackGate,
    markVisibleRange,
    publishSnapshot,
    speed,
    updateAttentionQualification,
  ]);

  useEffect(() => {
    if (!enabled || !isPlaying) {
      return;
    }

    let lastScrollAt = Date.now();
    const intervalId = window.setInterval(() => {
      const viewport = viewportRef.current;
      const pauseReason = getPauseReason();

      if (!viewport || pauseReason) {
        logPlaybackGate('autoscroll-skipped', {
          hasViewport: Boolean(viewport),
          isPlaying,
          pauseReason,
        });
        lastScrollAt = Date.now();
        return;
      }

      const now = Date.now();
      const elapsedMs = Math.min(500, now - lastScrollAt);
      lastScrollAt = now;
      const { maximumLeft, maximumTop } = getViewportScrollLimits(viewport);

      const distance =
        (BASE_SCROLL_PIXELS_PER_SECOND *
          normalizePlaybackSpeed(speed) *
          elapsedMs) /
        1000;
      const readingWindowComplete = advanceReadingWindow(viewport, distance);

      if (maximumTop <= 0) {
        if (readingWindowComplete) {
          bottomReachedRef.current = true;
        }
        return;
      }

      if (scrollMode === 'zPath' && maximumLeft > 2) {
        if (zPathPhaseRef.current === 'reveal') {
          if (readingWindowComplete) {
            zPathPhaseRef.current = 'scan';
          }
        } else if (zPathPhaseRef.current === 'scan') {
          if (viewport.scrollLeft < maximumLeft - 2) {
            viewport.scrollLeft = Math.min(
              maximumLeft,
              viewport.scrollLeft + distance
            );
          } else if (viewport.scrollTop >= maximumTop - 2) {
            bottomReachedRef.current = true;
          } else {
            zPathPhaseRef.current = 'return';
            zPathReturnTargetTopRef.current = Math.min(
              maximumTop,
              viewport.scrollTop +
                Math.max(1, viewport.clientHeight - Z_PATH_VERTICAL_OVERLAP_PX)
            );
          }
        } else {
          const returnDistance = distance * Z_PATH_RETURN_SPEED_MULTIPLIER;
          const targetTop = zPathReturnTargetTopRef.current ?? maximumTop;

          viewport.scrollLeft = Math.max(
            0,
            viewport.scrollLeft - returnDistance
          );
          viewport.scrollTop = Math.min(
            targetTop,
            viewport.scrollTop + returnDistance
          );

          if (viewport.scrollLeft <= 2 && viewport.scrollTop >= targetTop - 2) {
            viewport.scrollLeft = 0;
            viewport.scrollTop = targetTop;
            zPathReturnTargetTopRef.current = null;
            zPathPhaseRef.current = 'reveal';
            resetReadingWindow(viewport);
          }
        }
      } else if (viewport.scrollTop >= maximumTop - 2) {
        bottomReachedRef.current = true;
      } else {
        viewport.scrollTop = Math.min(
          maximumTop,
          viewport.scrollTop + distance
        );
      }
      updateReadingWindowBottom(viewport);
    }, AUTOSCROLL_TICK_MS);

    return () => window.clearInterval(intervalId);
  }, [
    enabled,
    advanceReadingWindow,
    getPauseReason,
    isPlaying,
    logPlaybackGate,
    resetReadingWindow,
    scrollMode,
    speed,
    updateReadingWindowBottom,
  ]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) {
      return;
    }

    viewport.scrollTop = 0;
    viewport.scrollLeft = 0;
    bottomReachedRef.current = false;
    zPathPhaseRef.current = 'reveal';
    zPathReturnTargetTopRef.current = null;
    resetReadingWindow(viewport);
  }, [resetReadingWindow, scrollMode]);

  const handleScroll = useCallback(() => {
    if (!getPauseReason()) {
      markVisibleRange();
      publishSnapshot(null);
    }
  }, [getPauseReason, markVisibleRange, publishSnapshot]);

  const togglePlaying = useCallback(() => {
    setIsPlaying((current) => {
      const viewport = viewportRef.current;

      if (!current) {
        releaseAttentionPlaybackGate('manual-play');
      } else {
        attentionPausedPlaybackRef.current = false;
      }

      if (!current && viewport) {
        const { maximumTop } = getViewportScrollLimits(viewport);
        const isAtBottom =
          maximumTop > 0 && viewport.scrollTop >= maximumTop - 4;

        if (isAtBottom) {
          viewport.scrollTop = 0;
          viewport.scrollLeft = 0;
          bottomReachedRef.current = false;
          zPathPhaseRef.current = 'reveal';
          zPathReturnTargetTopRef.current = null;
          resetReadingWindow(viewport);
          coverageRef.current = new Set<number>();
          qualifiedDurationRef.current = 0;
        }
      }

      return !current;
    });
  }, [releaseAttentionPlaybackGate, resetReadingWindow]);

  const getSnapshot = useCallback(() => snapshotRef.current, []);

  return {
    viewportRef: viewportRef as RefObject<HTMLDivElement>,
    snapshot,
    speed,
    setSpeed,
    isPlaying,
    readingWindowBottomPx,
    togglePlaying,
    handleScroll,
    getSnapshot,
  };
};
